import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Statistic,
    Typography,
    Tag,
    Button,
    Table,
    Spin,
    Timeline,
    Layout,
    Space
} from 'antd';
import {
    DollarOutlined,
    ShoppingOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    RiseOutlined,
    PlusOutlined,
    CheckCircleOutlined,
    UserOutlined,
    TagOutlined,
    MailOutlined,
    PlayCircleOutlined,
    ShopOutlined
} from '@ant-design/icons';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    AreaChart
} from 'recharts';
import moment from 'moment';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import PageHeader from '../../components/Common/PageHeader';

const { Title, Text } = Typography;
const { Content } = Layout;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Dashboard = () => {
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingAuctions, setLoadingAuctions] = useState(true);
    const [loadingActivity, setLoadingActivity] = useState(true);
    const [stats, setStats] = useState({
        totalAuctions: 0,
        activeAuctions: 0,
        totalLots: 0,
        totalBids: 0
    });
    const [recentAuctions, setRecentAuctions] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [chartData, setChartData] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoadingStats(true);
            setLoadingAuctions(true);
            setLoadingActivity(true);

            const [statsResponse, activityResponse, analyticsResponse] = await Promise.all([
                auctionService.getStats(),
                auctionService.getRecentActivity(),
                auctionService.getAnalytics()
            ]);

            setStats(statsResponse.data);
            setRecentActivity(activityResponse.data);
            setRecentAuctions(analyticsResponse.recent_auctions);
            setChartData({});

            setLoadingStats(false);
            setLoadingAuctions(false);
            setLoadingActivity(false);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            setLoadingStats(false);
            setLoadingAuctions(false);
            setLoadingActivity(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            draft: 'default',
            scheduled: 'processing',
            active: 'success',
            completed: 'success',
            cancelled: 'error'
        };
        return colors[status] || 'default';
    };

    const recentAuctionsColumns = [
        {
            title: <span style={{ fontWeight: 600, fontSize: '14px' }}>Title</span>,
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => (
                <a 
                    onClick={() => navigate(`/auctions/${record.slug}`)}
                    style={{ 
                        color: '#1890ff',
                        fontWeight: 500,
                        textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                    }}
                >
                    {text}
                </a>
            )
        },
        {
            title: <span style={{ fontWeight: 600, fontSize: '14px' }}>Status</span>,
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ borderRadius: '6px', fontWeight: 500 }}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: <span style={{ fontWeight: 600, fontSize: '14px' }}>Lots</span>,
            dataIndex: 'lots_count',
            key: 'lots_count',
            render: (count) => (
                <span style={{ fontWeight: 500, color: '#595959' }}>{count || 0}</span>
            )
        },
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Dashboard"
                description="Welcome to your auction dashboard"
            />

            <Row gutter={[24, 24]}>
                {/* Statistics Cards */}
                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.25)',
                            border: 'none',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '28px' }}
                        hoverable
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.35)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.25)';
                        }}
                    >
                        <Spin spinning={loadingStats}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <ShoppingOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                            </div>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500, letterSpacing: '0.3px' }}>Total Auctions</span>}
                                value={stats.totalAuctions}
                                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: '700', lineHeight: '1.2' }}
                            />
                        </Spin>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(240, 147, 251, 0.25)',
                            border: 'none',
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '28px' }}
                        hoverable
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(240, 147, 251, 0.35)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(240, 147, 251, 0.25)';
                        }}
                    >
                        <Spin spinning={loadingStats}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <ClockCircleOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                            </div>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500, letterSpacing: '0.3px' }}>Active Auctions</span>}
                                value={stats.activeAuctions}
                                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: '700', lineHeight: '1.2' }}
                            />
                        </Spin>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(79, 172, 254, 0.25)',
                            border: 'none',
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '28px' }}
                        hoverable
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(79, 172, 254, 0.35)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(79, 172, 254, 0.25)';
                        }}
                    >
                        <Spin spinning={loadingStats}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <TagOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                            </div>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500, letterSpacing: '0.3px' }}>Total Lots</span>}
                                value={stats.totalLots}
                                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: '700', lineHeight: '1.2' }}
                            />
                        </Spin>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        style={{ 
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(250, 112, 154, 0.25)',
                            border: 'none',
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: '28px' }}
                        hoverable
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(250, 112, 154, 0.35)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(250, 112, 154, 0.25)';
                        }}
                    >
                        <Spin spinning={loadingStats}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <DollarOutlined style={{ color: 'white', fontSize: '24px' }} />
                                </div>
                            </div>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500, letterSpacing: '0.3px' }}>Total Bids</span>}
                                value={stats.totalBids}
                                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: '700', lineHeight: '1.2' }}
                            />
                        </Spin>
                    </Card>
                </Col>

                {/* Recent Auctions */}
                <Col xs={24} lg={16}>
                    <Card
                        style={{ 
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: 0 }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #f0f7ff',
                            paddingBottom: 20,
                            marginBottom: 0,
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                            padding: '24px 32px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    padding: '10px',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                }}>
                                    <ShoppingOutlined style={{ color: 'white', fontSize: '20px' }} />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                        Recent Auctions
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '14px', color: '#595959', display: 'block', marginTop: 2 }}>
                                        Your latest auction activity
                                    </Text>
                                </div>
                            </Space>
                            <Button 
                                type="primary" 
                                onClick={() => navigate('/my-auctions')}
                                style={{ 
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                }}
                            >
                                View All
                            </Button>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <Spin spinning={loadingAuctions}>
                                <Table
                                    columns={recentAuctionsColumns}
                                    dataSource={recentAuctions}
                                    rowKey="id"
                                    pagination={false}
                                    style={{ 
                                        background: 'transparent'
                                    }}
                                />
                            </Spin>
                        </div>
                    </Card>
                </Col>

                {/* Recent Activity */}
                <Col xs={24} lg={8}>
                    <Card 
                        style={{ 
                            borderRadius: '16px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e8f4f8',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        bodyStyle={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }}
                    >
                        <div style={{ 
                            borderBottom: '2px solid #f0f7ff',
                            paddingBottom: 20,
                            marginBottom: 0,
                            background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                            padding: '24px 32px'
                        }}>
                            <Space align="center" size="middle">
                                <div style={{
                                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                    padding: '10px',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)'
                                }}>
                                    <ClockCircleOutlined style={{ color: 'white', fontSize: '20px' }} />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                        Recent Activity
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: '14px', color: '#595959', display: 'block', marginTop: 2 }}>
                                        Latest updates and notifications
                                    </Text>
                                </div>
                            </Space>
                        </div>
                        <div style={{ 
                            padding: '24px',
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Spin spinning={loadingActivity}>
                                <div className="recent-activity-scroll" style={{ 
                                    flex: 1,
                                    maxHeight: '400px', 
                                    overflowY: 'auto',
                                    paddingRight: '8px'
                                }}>
                                    <Timeline
                                        style={{ marginTop: '8px' }}
                                    >
                                        {recentActivity.slice(0, 5).map((activity, index) => {
                                        // Custom icon and color for my-invitation
                                        let icon = <TagOutlined style={{ fontSize: '14px' }} />;
                                        let color = '#1890ff';
                                        let label = '';
                                        if (activity.type === 'bid') {
                                            icon = <RiseOutlined style={{ fontSize: '14px' }} />;
                                            color = '#52c41a';
                                            label = 'New Bid';
                                        } else if (activity.type === 'invitation') {
                                            icon = <UserOutlined style={{ fontSize: '14px' }} />;
                                            color = '#fa8c16';
                                            label = 'Invitation Response';
                                        } else if (activity.type === 'auction') {
                                            icon = <TagOutlined style={{ fontSize: '14px' }} />;
                                            color = '#1890ff';
                                            label = 'Auction Update';
                                        } else if (activity.type === 'my-invitation') {
                                            icon = <MailOutlined style={{ fontSize: '14px' }} />;
                                            if (activity.description && activity.description.includes('pending')) {
                                                color = '#1890ff';
                                            } else if (activity.description && activity.description.includes('accepted')) {
                                                color = '#52c41a';
                                            } else if (activity.description && activity.description.includes('declined')) {
                                                color = '#f5222d';
                                            }
                                            label = 'My Invitation';
                                        } else if (activity.type === 'start-bid') {
                                            icon = <PlayCircleOutlined style={{ fontSize: '14px' }} />;
                                            color = '#1890ff';
                                            label = 'Start Bid';
                                        } else if (activity.type === 'vendor-bid') {
                                            icon = <ShopOutlined style={{ fontSize: '14px' }} />;
                                            color = '#ff9800';
                                            label = 'Vendor Bid';
                                        }
                                        return (
                                            <Timeline.Item
                                                key={index}
                                                color={color}
                                                dot={icon}
                                            >
                                                <div style={{ 
                                                    backgroundColor: '#fafafa', 
                                                    padding: '14px 16px', 
                                                    borderRadius: '10px',
                                                    border: '1px solid #e8f4f8',
                                                    marginBottom: '12px',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                                    e.currentTarget.style.borderColor = '#d6e4ff';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.04)';
                                                    e.currentTarget.style.borderColor = '#e8f4f8';
                                                }}
                                                >
                                                    <Space direction="vertical" size={6}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                                            <Text strong style={{ fontSize: '14px', color: '#1a1a1a', fontWeight: 600 }}>{label}</Text>
                                                            <Text type="secondary" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                                {moment(activity.created_at).format('MMM D, h:mm A')}
                                                            </Text>
                                                        </div>
                                                        <Text style={{ fontSize: '13px', color: '#595959', lineHeight: '1.5' }}>{activity.description}</Text>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {moment(activity.created_at).fromNow()}
                                                        </Text>
                                                    </Space>
                                                </div>
                                            </Timeline.Item>
                                        );
                                        })}
                                    </Timeline>
                                </div>
                            </Spin>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Custom Scrollbar Styles */}
            <style>{`
                /* Custom Scrollbar for Recent Activity */
                .recent-activity-scroll::-webkit-scrollbar {
                    width: 6px;
                }

                .recent-activity-scroll::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 10px;
                }

                .recent-activity-scroll::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 10px;
                    transition: background 0.3s ease;
                }

                .recent-activity-scroll::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }

                /* Firefox scrollbar */
                .recent-activity-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: #cbd5e1 #f1f5f9;
                }
            `}</style>
        </DashboardLayout>
    );
};

export default Dashboard; 